import { Component, OnInit } from '@angular/core';
import { Chart } from 'chart.js';
import ky from 'ky';
import { GoogleAnalyticsService } from 'ngx-google-analytics';
import { FlyffQuestList } from 'src/app/Models/dto/flyff-quest';

interface Quest {
  id: number;
  xpReward: number;
  minLvl: number;
  maxLvl: number;
  npc: number;
}

@Component({
  selector: 'app-daily-calculator',
  templateUrl: './daily-calculator.component.html',
  styleUrls: ['../../shared-scss/default.scss'],
  standalone: false,
})
export class DailyCalculatorComponent implements OnInit {
  constructor(private readonly gaService: GoogleAnalyticsService) {}

  startingLevel: number = 86;

  startingXp: number = 0;
  targetLevel: number = 190;
  targetXp: number = 0;
  quests: Quest[] = [];
  daysPerLevel: { [level: number]: number } = {};
  xpPerLevel: { [level: number]: number } = {};
  towerXpPerLevel: { [level: number]: number } = {};
  eillunXpPerLevel: { [level: number]: number } = {};

  doTowerUntil: number = 190;
  noXpAvailable: boolean = false;
  lastReachedLevel: number = 0;

  ngOnInit(): void {
    this.fetchQuests();
  }

  myChart: Chart | undefined = undefined;

  GenerateChart(xpPerDayMode: boolean) {
    if (xpPerDayMode) {
      this.GenerateXpChart();
    } else {
      this.GenerateDaysChart();
    }
  }

  GenerateXpChart() {
    if (this.myChart) {
      this.myChart.destroy();
    }

    const chartData: any = {
      labels: this.filteredXpPerDay.map((d) => d.level.toString()),
      datasets: [
        {
          label: 'Tower XP',
          data: this.filteredXpPerDay.map(
            (d) => this.towerXpPerLevel[d.level] || 0
          ),
          backgroundColor: 'rgba(54, 162, 235, 0.8)',
          borderColor: 'rgba(54, 162, 235, 1)',
          borderWidth: 1,
        },
        {
          label: 'Eillun XP',
          data: this.filteredXpPerDay.map(
            (d) => this.eillunXpPerLevel[d.level] || 0
          ),
          backgroundColor: 'rgba(255, 99, 132, 0.8)',
          borderColor: 'rgba(255, 99, 132, 1)',
          borderWidth: 1,
        },
      ],
    };

    const config: any = {
      type: 'bar',
      data: chartData,
      options: {
        scales: {
          x: {
            stacked: true,
          },
          y: {
            stacked: true,
            beginAtZero: true,
          },
        },
      },
    };

    const chartElem = <HTMLCanvasElement>(
      document.getElementById(`upgrade-data-chart`)
    );

    this.myChart = new Chart(chartElem, config);
  }

  GenerateDaysChart() {
    if (this.myChart) {
      this.myChart.destroy();
    }

    const chartData: any = {
      labels: this.filteredDaysPerLevel.map((d) => d.level.toString()),
      datasets: [
        {
          label: 'days taken',
          data: this.filteredDaysPerLevel.map((d) => d.days),
          borderWidth: 1,
        },
      ],
    };

    const config: any = {
      type: 'bar',
      data: chartData,
      options: {
        scales: {
          y: {
            beginAtZero: true,
          },
        },
      },
    };

    const chartElem = <HTMLCanvasElement>(
      document.getElementById(`upgrade-data-chart`)
    );

    this.myChart = new Chart(chartElem, config);
  }

  async calculateDailyDuration(
    xpPerDayMode: boolean,
    isUserInput = true
  ): Promise<number> {
    if (isUserInput) {
      this.gaService.event(
        'simulate_ulti_weapon_upgrade',
        'simulate',
        'Simulate ultimate weapon upgrade',
        1,
        true
      );
    }

    this.targetXp = Math.min(this.targetXp, 100);
    this.targetXp = Math.max(this.targetXp, 0);

    this.startingXp = Math.min(this.startingXp, 100);
    this.startingXp = Math.max(this.startingXp, 0);

    this.startingLevel = Math.max(this.startingLevel, 86);
    this.startingLevel = Math.min(this.startingLevel, 190);

    this.targetLevel = Math.max(this.targetLevel, this.startingLevel);
    this.targetLevel = Math.min(this.targetLevel, 190);

    let currentLevel = this.startingLevel;
    let currentXp = this.startingXp;
    let totalDays = 0;
    this.daysPerLevel = {};
    this.noXpAvailable = false;

    while (
      currentLevel < this.targetLevel ||
      (currentLevel === this.targetLevel && currentXp < this.targetXp)
    ) {
      const availableQuests = this.getAvailableQuests(currentLevel);
      let dailyTowerXp = 0;
      let dailyEillunXp = 0;
      for (const quest of availableQuests) {
        if (this.isTowerQuest(quest)) {
          dailyTowerXp += this.calculateXpReward(currentLevel, quest);
        } else {
          dailyEillunXp += this.calculateXpReward(currentLevel, quest);
        }
      }

      if (dailyTowerXp + dailyEillunXp === 0) {
        // No quests give XP at this level (e.g. tower disabled via
        // doTowerUntil and no non-tower quests cover this level range),
        // so further looping would never make progress.
        this.noXpAvailable = true;
        this.lastReachedLevel = currentLevel;
        break;
      }

      currentXp += dailyTowerXp + dailyEillunXp;
      this.xpPerLevel[currentLevel] = dailyTowerXp + dailyEillunXp;
      this.towerXpPerLevel[currentLevel] = dailyTowerXp;
      this.eillunXpPerLevel[currentLevel] = dailyEillunXp;

      // Level up as much as possible
      while (currentLevel < this.targetLevel && currentXp >= 100) {
        currentXp -= 100;
        currentLevel++;
      }

      // If we've reached the target level and have enough XP, stop
      if (currentLevel === this.targetLevel && currentXp >= this.targetXp) {
        break;
      }

      this.daysPerLevel[currentLevel] =
        (this.daysPerLevel[currentLevel] || 0) + 1;
      totalDays++;
    }

    this.GenerateChart(xpPerDayMode);
    return totalDays;
  }

  async fetchQuests() {
    const json = await ky
      .post('https://flyffipedia.com/api/static/quests', {
        json: {
          lang: 0,
          page: 0,
          pageSize: 200,
          sortLabel: 'minLevel',
          sortDirection: 'Ascending',
          searchString: null,
          currentLevel: 86,
          level: null,
          minLevel: null,
          exactMinLevel: null,
          types: ['Daily'],
          rewards: ['EXP'],
          repeatable: null,
          partyShare: null,
        },
      })
      .json<FlyffQuestList>();

    const data = this.convertQuestData(json);
    this.quests = data;

    this.calculateDailyDuration(false, false);
  }

  calculateXpReward(currentLevel: number, quest: Quest): number {
    const overlevel = currentLevel - quest.minLvl;
    if (quest.minLvl < 100) {
      return quest.xpReward / (1.37 - 0.00267 * quest.minLvl) ** overlevel;
    }
    return quest.xpReward / 1.1 ** overlevel;
  }

  getAvailableQuests(currentLevel: number) {
    let quests = this.quests.filter(
      (q) => currentLevel >= q.minLvl && currentLevel <= q.maxLvl
    );
    if (this.doTowerUntil <= currentLevel) {
      quests = quests.filter((q) => !this.isTowerQuest(q));
    }
    return quests;
  }

  convertQuestData(json: FlyffQuestList): Quest[] {
    return json.quests.map((q) => ({
      id: q.id,
      xpReward: Math.max(...q.endReceiveExperience),
      minLvl: q.minLevel,
      maxLvl: q.maxLevel,
      npc: q.beginNPC,
    }));
  }

  isTowerQuest(quest: Quest): boolean {
    return quest.npc == 11734;
  }

  get filteredXpPerDay() {
    return Object.entries(this.xpPerLevel)
      .filter(([level, xp]) => xp > 0 && +level >= this.startingLevel && +level <= this.targetLevel)
      .map(([level, xp]) => ({ level: +level, xp }));
  }

  get filteredDaysPerLevel() {
    return Object.entries(this.daysPerLevel)
      .filter(([level, days]) => days > 0)
      .map(([level, days]) => ({ level: +level, days }));
  }

  get totalDaysNeeded(): number {
    return this.filteredDaysPerLevel.reduce(
      (acc, level) => acc + level.days,
      0
    );
  }
}
